import {computed, Signal} from '@angular/core';
import {signalStoreFeature, withComputed, withState} from '@ngrx/signals';

export type RequestStatus = 'idle' | 'pending' | 'fulfilled' | {error: string};

type RequestStatusState<Name extends string> = {
  [K in `${Name}_status`]: RequestStatus;
};

type CamelCase<S extends string> = S extends `${infer Head}_${infer Tail}`
  ? `${Head}${Capitalize<CamelCase<Tail>>}`
  : S;

type RequestStatusComputed<Name extends string> =
  & {[K in `is${Capitalize<CamelCase<Name>>}Pending`]: Signal<boolean>}
  & {[K in `is${Capitalize<CamelCase<Name>>}Fulfilled`]: Signal<boolean>}
  & {[K in `${CamelCase<Name>}Error`]: Signal<string | null>};

const statusKey = <Name extends string>(name: Name) => `${name}_status` as const;
const camelCase = (name: string) => name.replace(/_(\w)/g, (_, char: string) => char.toUpperCase());
const capitalize = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);

/**
 * Tracks one named request's lifecycle. withRequestStatus('search') adds:
 * - state: `search_status`
 * - computed: `isSearchPending`, `isSearchFulfilled`, `searchError`
 * Snake case names are camel cased for the computed, 'compare_addons' gives `isCompareAddonsPending`.
 * Update it with patchState(store, setPending('search')) and the other updaters below.
 */
export function withRequestStatus<Name extends string>(name: Name) {
  return signalStoreFeature(
    withState({[statusKey(name)]: 'idle'} as RequestStatusState<Name>),
    withComputed((store) => {
      const status = (store as Record<string, unknown>)[statusKey(name)] as Signal<RequestStatus>;
      const camelName = camelCase(name);

      return {
        [`is${capitalize(camelName)}Pending`]: computed(() => status() === 'pending'),
        [`is${capitalize(camelName)}Fulfilled`]: computed(() => status() === 'fulfilled'),
        [`${camelName}Error`]: computed(() => {
          const current = status();
          return typeof current === 'object' ? current.error : null;
        }),
      } as RequestStatusComputed<Name>;
    }),
  );
}

export function setIdle<Name extends string>(name: Name): RequestStatusState<Name> {
  return {[statusKey(name)]: 'idle'} as RequestStatusState<Name>;
}

export function setPending<Name extends string>(name: Name): RequestStatusState<Name> {
  return {[statusKey(name)]: 'pending'} as RequestStatusState<Name>;
}

export function setFulfilled<Name extends string>(name: Name): RequestStatusState<Name> {
  return {[statusKey(name)]: 'fulfilled'} as RequestStatusState<Name>;
}

export function setError<Name extends string>(name: Name, error: string): RequestStatusState<Name> {
  return {[statusKey(name)]: {error}} as RequestStatusState<Name>;
}
